import { useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from 'react-query'
import { useAuthStore } from './store/authStore'

// Common components
import LoginPage from './pages/LoginPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import ProtectedRoute from './components/common/ProtectedRoute'
import LoadingScreen from './components/common/LoadingScreen'

// Admin components
import AdminLayout from './components/admin/layout/Layout'
import AdminDashboardPage from './pages/admin/dashboard/DashboardPage'
// import DataCategoriesPage from './pages/admin/data-categories/DataCategoriesPage'
// import AdminSettingsPage from './pages/admin/settings/SettingsPage'

// Finance components
import FinanceLayout from './components/finance/layout/FinanceLayout'
import FinanceDashboardPage from './pages/finance/dashboard/FinanceDashboardPage'
import ClientsPage from './pages/finance/clients/ClientsPage'
import ReportsPage from './pages/finance/reports/ReportsPage'
import PortfolioPage from './pages/finance/portfolio/PortfolioPage'
import { ClientProfilePage } from './pages/finance/client-profile/ClientProfilePage'

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
})

const App = () => {
  const { isAuthenticated, isLoading, currentPortal, initializeAuth } = useAuthStore()

  // Initialize authentication on app start
  useEffect(() => {
    const hasInitialized = sessionStorage.getItem('auth-initialized')
    if (!hasInitialized) {
      initializeAuth().finally(() => {
        sessionStorage.setItem('auth-initialized', 'true')
      })
    }
  }, [initializeAuth])

  // Show loading screen while checking authentication
  if (isLoading) {
    return <LoadingScreen />
  }

  // 404 Page Component
  const NotFoundPage = () => {
    const errorConfig = {
      admin: {
        bgGradient: 'bg-gradient-to-br from-blue-50 to-indigo-100',
        titleColor: 'text-blue-900',
        textColor: 'text-blue-700',
        buttonColor: 'bg-blue-600 hover:bg-blue-700'
      },
      finance: {
        bgGradient: 'bg-gradient-to-br from-emerald-50 to-teal-100',
        titleColor: 'text-emerald-900',
        textColor: 'text-emerald-700',
        buttonColor: 'bg-emerald-600 hover:bg-emerald-700'
      },
      default: {
        bgGradient: 'bg-gray-50',
        titleColor: 'text-gray-900',
        textColor: 'text-gray-600',
        buttonColor: 'bg-indigo-600 hover:bg-indigo-700'
      }
    }

    const config = currentPortal ? errorConfig[currentPortal] : errorConfig.default

    return (
      <div className={`min-h-screen flex items-center justify-center ${config.bgGradient}`}>
        <div className="text-center">
          <h1 className={`text-4xl font-bold ${config.titleColor} mb-4`}>404</h1>
          <p className={`${config.textColor} mb-8`}>Page not found</p>
          <button
            onClick={() => window.history.back()}
            className={`px-6 py-3 ${config.buttonColor} text-white rounded-lg transition-colors`}
          >
            Go Back
          </button>
        </div>
      </div>
    )
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <Routes>
          {/* Public routes */}
          <Route
            path="/login"
            element={
              isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />
            }
          />
          <Route
            path="/forgot-password"
            element={
              isAuthenticated ? <Navigate to="/dashboard" replace /> : <ForgotPasswordPage />
            }
          />
          <Route
            path="/reset-password"
            element={
              isAuthenticated ? <Navigate to="/dashboard" replace /> : <ResetPasswordPage />
            }
          />

          {/* Admin protected routes */}
          {isAuthenticated && currentPortal === 'admin' && (
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<AdminDashboardPage />} />
              {/* <Route path="data-categories" element={<DataCategoriesPage />} />
              <Route path="settings" element={<AdminSettingsPage />} /> */}
            </Route>
          )}

          {/* Finance protected routes */}
          {isAuthenticated && currentPortal === 'finance' && (
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <FinanceLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<FinanceDashboardPage />} />
              <Route path="clients" element={<ClientsPage />} />
              <Route path="client-profile/:clientId" element={<ClientProfilePage />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="portfolio" element={<PortfolioPage />} />
            </Route>
          )}

          {/* Default redirect to login if not authenticated */}
          <Route
            path="/"
            element={
              isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
            }
          />

          {/* Catch all unauthenticated routes and redirect to login */}
          {!isAuthenticated && (
            <Route path="*" element={<Navigate to="/login" replace />} />
          )}

          {/* 404 Route for authenticated users only */}
          {isAuthenticated && (
            <Route path="*" element={<NotFoundPage />} />
          )}
        </Routes>
      </Router>
    </QueryClientProvider>
  )
}

export default App
